import { memo, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import AppGlobalsContext from "app/AppGlobalsContext";
import "./ImagesCarousel.scss";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import Swiper from "react-id-swiper";
import CarouselDots from "app/pages/.shared/Carousel/CarouselDots/CarouselDots";
import CarouselArrow from "app/pages/SmartDP/Listing/SmartDPProduct/ImagesCarousel/CarouselArrow";
import SwiperLazyBackgroundImage from "app/pages/SmartDP/Listing/SmartDPProduct/ImagesCarousel/SwiperLazyBackgroundImage";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { ICON_SIZES } from "app/constants";

const IMG_SIZES_MOBILE = {
	small: { width: 768, height: 416 },
	medium: { width: 768, height: 416 },
};

const IMG_SIZES_DESKTOP = {
	large: { width: 406, height: 180 },
	xlarge: { width: 406, height: 180 },
};

const CLOUDINARY_OPTIONS = ["c_fill"];

const ImagesCarousel = ({
	images = [],
	alt,
	imgSizesMobile = IMG_SIZES_MOBILE,
	imgSizesDesktop = IMG_SIZES_DESKTOP,
	showImageIndex = false,
	onClick = () => {},
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const [currentSlide, setCurrentSlide] = useState(0);
	const isMobile = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;

	const [swiper, setSwiper] = useState(null);

	const updateSwiper = swiper => {
		setSwiper(swiper);
	};

	const slidePrev = () => {
		swiper.slidePrev();
	};

	const slideNext = () => {
		swiper.slideNext();
	};

	const swiperParams = {
		shouldSwiperUpdate: true,
		getSwiper: updateSwiper,
		slidesPerView: 1,
		pagination: {
			el: ".swiper-pagination",
			type: "bullets",
			clickable: true,
		},
		loop: images?.length > 1,
		preloadImages: false,
		lazy: { loadPrevNext: true, loadPrevNextAmount: 4, loadOnTransitionStart: true },
	};

	useEffect(() => {
		if (swiper) {
			swiper.on("slideChangeTransitionStart", () => {
				let currentSlide = swiper?.activeIndex - 1;
				if (swiper?.activeIndex > images.length) {
					currentSlide = 0;
				}
				if (swiper?.activeIndex === 0) {
					currentSlide = images.length - 1;
				}
				setCurrentSlide(currentSlide);
			});
		}

		return () => {
			if (swiper) {
				swiper.off("slideChangeTransitionEnd");
			}
		};
	}, [swiper]);

	return (
		<div className="images-carousel">
			<Swiper {...swiperParams}>
				{images.map((image, index) => {
					return (
						<div onClick={onClick} className="images-carousel__image" key={index}>
							<SwiperLazyBackgroundImage
								sizes={isMobile ? imgSizesMobile : imgSizesDesktop}
								cloudinaryOptions={CLOUDINARY_OPTIONS}
								alt={alt}
								resolution={resolution}
								src={image.url}
								backgroundPosition="center"
							/>
						</div>
					);
				})}
			</Swiper>
			{images?.length > 1 && swiper && (
				<>
					<div className="images-carousel__button--next" onClick={slideNext}>
						<CarouselArrow
							orientation="right"
							className="images-carousel"
							size={ICON_SIZES.MD}
						/>
					</div>
					<div className="images-carousel__button--back" onClick={slidePrev}>
						<CarouselArrow
							orientation="left"
							className="images-carousel"
							size={ICON_SIZES.MD}
						/>
					</div>
					{showImageIndex ? (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.REGULAR}
							className="images-carousel__image-index"
							component="span"
							isBold
						>
							{`${currentSlide + 1}/${images?.length}`}
						</Typography>
					) : (
						<CarouselDots
							currentSlide={currentSlide}
							totalSlides={images?.length}
							size={isMobile ? 7 : 8}
						/>
					)}
				</>
			)}
		</div>
	);
};

ImagesCarousel.propTypes = {
	images: PropTypes.array,
	imgSizesMobile: PropTypes.object,
	imgSizesDesktop: PropTypes.object,
	alt: PropTypes.string,
	showImageIndex: PropTypes.bool,
	onClick: PropTypes.func,
};

export default memo(ImagesCarousel);
